import React, { useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useChangeProduct, useImportTableUid, useUpdateOrderPricePreview } from '@/hooks';
import { useOrderExcelRowByUID, useUpdateOrderExcelRow } from '@/hooksApi/useImport';
import { Store } from 'antd/es/form/interface';
import {
  adjustProductListByOrderType,
  arrayToCommaSeparatedObject,
  attachCountryCodeToPhone,
  commaSeparatedObjectToArray,
  checkProductValidation,
  getOrderTypeTextKey,
  parsePhoneNumber,
  scrollTop,
} from '@/utils';
import { toFeeCustomerPayable } from '@/utils/importCustomFee';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import {
  CustomerDetailsCard,
  DeliveryInstructionCard,
  OrderIdCard,
  PackageAndPaymentCard,
  ProductDetailsCard,
} from '../OrderForm';
import { BasicProduct, OrderExcelRow, PaymentType, ProductDetails, ShipmentFeeType } from '@/types';
import ServiceSelectCard from './ServiceSelectCard';
import CustomerShippingFeeField from './CustomerShippingFeeField';
import useProductStore from '@/stores/productStore';
import useUserStore, { selectUsesServices } from '@/stores/userStore';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';

interface ModalProps {
  editingOrderUid?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditOrderForm: React.FC<ModalProps> = ({ editingOrderUid, onClose, onSuccess }) => {
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string | undefined>(undefined);

  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const usesServices = useUserStore(selectUsesServices);

  const tableUid = useImportTableUid((state) => state.importTableUid); // Access the Table UID setter from Zustand
  const { orderType, resetOrderType, setOrderType } = useOrderTypeStore();

  const { mutate: updateOrder, isLoading: updateLoading } = useUpdateOrderExcelRow(
    tableUid,
    editingOrderUid || '',
  );

  const { data: orderRow, isLoading: orderRowLoading } = useOrderExcelRowByUID(
    tableUid,
    editingOrderUid || '',
  );

  const { data: provinceList } = useFetchListProvinces();

  const { data: areaList, isLoading: areaListLoading } = useFetchAreaByProvinceCode(
    selectedProvinceCode!,
  );

  // BULK-SVC — hoisted above the `orderRowLoading` early return. Called from
  // inside the JSX it would be a conditional hook.
  const temperatureControl = Form.useWatch<boolean | undefined>('temperatureControl', form);
  // BOXY-414 — the row's custom customer fee, live, so the summary's
  // "customer will pay" moves as the merchant types.
  const shipmentFeeType = Form.useWatch<ShipmentFeeType | undefined>('CustomerOrMerchant', form);
  const rawCustomFee = Form.useWatch<string | number | undefined>('customShipmentFee', form);
  const customFee = toFeeCustomerPayable(shipmentFeeType, rawCustomFee) ?? 0;

  const { products, resetProducts, setSubmitAttempted } = useProductStore();

  const {
    orderPrice,
    setOrderPrice,
    orderPriceLoading,
    previewBlockedReason,
    setCustomShipmentFee,
    setPackageSize,
    checkOrderFormValidation,
    handleProductChanged,
  } = useUpdateOrderPricePreview(products, form, true);

  const { handleAddProduct, handleDeleteProduct, setProducts, handleUpdateProduct } =
    useChangeProduct(
      (products_arg: ProductDetails[]) => handleProductChanged(products_arg, false),
      orderType,
    );

  useEffect(() => {
    // Reset on mount to clear any stale data from previous order flows
    form.resetFields();
    resetOrderType();
    resetProducts();

    return () => {
      form.resetFields();
      resetOrderType();
      resetProducts();

      queryClient.removeQueries(['orderExcelRow']);
    };
  }, []);

  useEffect(() => {
    if (!areaListLoading && areaList && areaList?.object.length > 0) {
      const area = areaList.object.find((item) => item.uid === orderRow?.object.region?.uid);

      form.setFieldsValue({ region_uid: area?.uid });
    }
  }, [areaListLoading, areaList, orderRow, editingOrderUid]);

  useEffect(() => {
    form.resetFields();

    if (orderRow && provinceList) {
      setOrderPrice({
        ...orderPrice,
        // ! Shipment fee is missing.
        // ! Elham mentioned that this is not required
        orders_value: orderRow.object.products_value ?? 0,
      });

      const initProducts = initializeProductList(orderRow.object);

      const initFormData = getInitialFormValue(orderRow.object);

      form.setFieldsValue(initFormData);
    }
  }, [orderRow, form, editingOrderUid, provinceList]);

  const getInitialFormValue = (orderData: OrderExcelRow) => {
    const provinceCode = provinceList?.object.find(
      (item) => item.province_code === orderData.region?.province_code,
    )?.province_code;
    const area_uid =
      provinceCode && provinceCode === selectedProvinceCode ? orderData.region?.uid : undefined;
    if (provinceCode && provinceCode !== selectedProvinceCode)
      setSelectedProvinceCode(provinceCode);

    const primaryPhoneInfo = parsePhoneNumber(orderData.phone);
    const secondaryPhoneInfo = parsePhoneNumber(orderData.secondary_phone ?? undefined);

    if (orderData.type) {
      setOrderType(orderData.type as OrderTypeEnum);
    }

    const data = {
      orderType: orderData.type,
      name: orderData.full_name || '',
      address: orderData.address_text || '',
      phoneNumberPrimary: primaryPhoneInfo.phone || '',
      countryCodePrimary: primaryPhoneInfo.countryCode,
      countryCodeSecondary: secondaryPhoneInfo?.countryCode,
      secondaryPhoneNumber: secondaryPhoneInfo?.phone,
      deliveryInstruction: orderData.description || '',
      packageSize: orderData.size || '',
      isFragile: orderData.is_fragile,
      temperatureControl: !!orderData.temperature_control,
      CodOrPrepaid: orderData.payment_type,
      CustomerOrMerchant: orderData.shipment_fee_type,
      orderId: orderData.custom_id,
      pickupOrDropoff: orderData.pick_up_type,
      province: provinceCode,
      region_uid: area_uid,
      isIdChecked: orderData.id_check ?? false,
      isClueVerification: !!orderData.clue_link_verification,
      // BULK-SVC — undefined (not null) so the Select renders its placeholder
      // "use my default" rather than an empty selected value.
      serviceCode: orderData.service_code ?? undefined,
      clueLink: orderData.clue_link_verification,
      customShipmentFee: orderData.fee_customer_payable ?? undefined,
      // saved_pick_up_address_uid: orderRow?.object?.pick_up_address.uid,
    };

    return data;
  };

  const initializeProductList = (orderData: OrderExcelRow) => {
    const commaSeparatedObj = {
      product_title: orderData?.product_title,
      product_price: orderData?.product_price,
      product_quantity: orderData?.product_quantity,
    };
    const productsArrayFromObject = commaSeparatedObjectToArray(commaSeparatedObj);

    if (productsArrayFromObject && productsArrayFromObject.length > 0) {
      const updatedProducts = adjustProductListByOrderType(
        productsArrayFromObject,
        orderData.type as OrderTypeEnum,
      );

      form.setFieldValue('products', updatedProducts);
      setProducts(updatedProducts);
      updatePricePreview(updatedProducts);
      return updatedProducts;
    }
    // Ensure at least 1 empty product row (Lovable UX pattern)
    const emptyProducts = [{ title: '', quantity: '1', price: '' } as BasicProduct];
    setProducts(emptyProducts);
    return emptyProducts;
  };

  const transformValuesToUpdateOrder = (values: Store) => {
    const commaSeparatedProduct = arrayToCommaSeparatedObject(
      adjustProductListByOrderType(values.products || products, values.orderType),
    );

    const primaryPhoneNumber = attachCountryCodeToPhone(
      values?.phoneNumberPrimary,
      values?.countryCodePrimary,
    );

    const secondaryPhoneNumber = attachCountryCodeToPhone(
      values?.secondaryPhoneNumber,
      values?.countryCodeSecondary,
    );

    return {
      type: values?.orderType,
      is_fragile: values?.isFragile || false,
      temperature_control: values?.temperatureControl || false,
      id_check: values?.isIdChecked || false,
      clue_link_verification: values?.isClueVerification ? (values.clueLink ?? null) : null,
      shipment_fee_type: values?.CustomerOrMerchant || ShipmentFeeType.BY_MERCHANT,
      payment_type: values?.CodOrPrepaid || PaymentType.COLLECT_ON_DELIVERY,
      description: values?.deliveryInstruction || '',
      custom_id: values?.orderId || '',
      size: values?.packageSize || 'S',
      full_name: values?.name || null,
      address_text: values?.address || null,
      phone: primaryPhoneNumber,
      secondary_phone: secondaryPhoneNumber,
      product_title: commaSeparatedProduct.product_title,
      product_quantity: commaSeparatedProduct.product_quantity,
      product_price: commaSeparatedProduct.product_price,
      region_uid: values.region_uid,
      // BULK-SVC — null, not omitted, when cleared: the merchant clearing the
      // field means "drop my per-row override and fall back to my rule/default",
      // and an omitted key would leave the old override in place.
      // No plan (uses_services=false): no service field, no service_code key.
      ...(usesServices ? { service_code: values?.serviceCode ?? null } : {}),
      // BOXY-414 — null, not omitted, when emptied: the row falls back to the
      // normal Boxy fee instead of keeping a stale (possibly product-price) one.
      fee_customer_payable: toFeeCustomerPayable(
        values?.CustomerOrMerchant,
        values?.customShipmentFee,
      ),
    };
  };

  const onFinish = async (values: Store) => {
    setSubmitAttempted(true);

    // Check for partially filled products (has some data but missing required fields)
    const partiallyFilled = products.filter((p) => {
      const hasTitle = !!p.title;
      const hasPrice = !!p.price || p.price === 0;
      return (hasTitle || hasPrice) && !(hasTitle && hasPrice);
    });

    if (partiallyFilled.length > 0) {
      message.error(t('please_fill_all_product_fields'));
      return;
    }

    // Filter out completely empty products
    const finalProducts = products.filter((p) => checkProductValidation(p));

    if (finalProducts.length === 0) {
      message.error(t('please_add_at_least_one_product'));
      return;
    }

    const isValid = checkOrderFormValidation({ ...values, products: finalProducts });
    if (!isValid) return;

    const orderPayload = transformValuesToUpdateOrder({ ...values, products: finalProducts });

    updateOrder(orderPayload, {
      onSuccess: () => {
        scrollTop();
        // Always invalidate to show the updated row immediately (PATCH already recalculates carriers)
        queryClient.invalidateQueries(['import']);
        queryClient.invalidateQueries(['import-valid']);
        queryClient.invalidateQueries(['import-invalid']);
        message.success(t('updated_successfully'));
        onSuccess?.();
        onClose();
      },
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Form submission failed:', errorInfo);
    // message.error(t('submission_failed'));
  };

  const handleSetSelectedProvinceCode = (provinceCode?: string) => {
    form.setFieldsValue({ province: provinceCode });
    form.setFieldsValue({ region_uid: undefined });
    form.setFieldValue('region_uid', undefined);
    setSelectedProvinceCode(provinceCode);
  };

  const handleCreateProduct = () => {
    handleAddProduct({ title: '', quantity: '1', price: '' });
  };

  const handleRemoveProduct = (index: number) => {
    const newProductList = handleDeleteProduct(index);
    updatePricePreview(newProductList);
  };

  const handleChangeProduct = (index: number, field: keyof BasicProduct, value: string) => {
    const newProductList = handleUpdateProduct(index, field, value);
    updatePricePreview(newProductList);
  };

  const handleOrderTypeChange = (newOrderType: OrderTypeEnum) => {
    const updatedProducts = adjustProductListByOrderType(products, newOrderType);
    setProducts(updatedProducts);
    updatePricePreview(updatedProducts);
  };

  const onFormValuesChange = (changedValues: any, allValues: Store) => {
    const changedItem = Object.keys(changedValues)[0];

    if (changedItem === 'orderType') {
      const updatedProducts = adjustProductListByOrderType(products, allValues.orderType);
      setProducts(updatedProducts);
      updatePricePreview(updatedProducts);
    }
  };

  const updatePricePreview = (updatedProducts: BasicProduct[]) => {
    setOrderPrice({
      ...orderPrice,
      orders_value: updatedProducts.reduce((acc, current) => {
        const quantity = Number(current.quantity) ?? 1;
        // Use parseFloat to avoid scientific notation for large numbers
        const price = parseFloat(String(current.price)) ?? 0;

        return acc + price * quantity;
      }, 0),
    });
  };

  const handleUpdatePreviewByContactSelection = () => {
    updatePricePreview(products);
  };

  if (orderRowLoading) {
    return <Spin size='large' className='flex justify-center items-center h-screen' />;
  } else
    return (
      <Form
        form={form}
        name='edit-row-order'
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={onFormValuesChange}
        className='edit-modal-form flex flex-col h-full overflow-hidden'
      >
        <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 px-6 py-6 flex-1 overflow-y-auto'>
          {/* Left column */}
          <div className='flex flex-col gap-6'>
            <CustomerDetailsCard
              handleUpdatePreview={handleUpdatePreviewByContactSelection}
              form={form}
              selectedProvinceCode={selectedProvinceCode}
              setSelectedProvinceCode={handleSetSelectedProvinceCode}
              disableContactSearch
            />
            <DeliveryInstructionCard />
            <OrderIdCard />
            <ProductDetailsCard
              products={products}
              handleCreateProduct={handleCreateProduct}
              handleDeleteProduct={handleRemoveProduct}
              handleUpdateProduct={handleChangeProduct}
            />
          </div>
          {/* Right column */}
          <div className='flex flex-col gap-6'>
            <PackageAndPaymentCard
              form={form}
              onPackageSizeChange={(size: string) => setPackageSize(size, false)}
              orderPrice={{ ...orderPrice, orders_custom_shipment_fee: customFee }}
              orderPriceLoading={orderPriceLoading}
              blockedReason={previewBlockedReason}
              paymentType={orderRow?.object?.payment_type}
              shipmentFeeType={orderRow?.object?.shipment_fee_type}
              packageSize={orderRow?.object?.size}
              idCheck={!!orderRow?.object?.id_check}
              clueVerificationLink={orderRow?.object?.clue_link_verification ?? undefined}
              onCustomShipmentFeeChange={setCustomShipmentFee}
              customShipmentFee={customFee || undefined}
              onOrderTypeChange={handleOrderTypeChange}
            />
            <CustomerShippingFeeField visible={shipmentFeeType === ShipmentFeeType.BY_CUSTOMER} />
            {usesServices ? <ServiceSelectCard temperatureControl={temperatureControl} /> : null}
          </div>
        </div>
        <div className='edit-modal-footer border-t border-solid border-[#e8e6e3] px-6 py-4 flex items-center justify-between bg-white shrink-0'>
          <div>
            {orderType !== OrderTypeEnum.NORMAL ? (
              <span className='text-xs font-medium text-[hsl(0,65%,45%)] uppercase'>
                {String(t(getOrderTypeTextKey(orderType)))}
              </span>
            ) : null}
          </div>
          <div className='flex items-center gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              {String(t('cancel'))}
            </button>
            <button
              type='submit'
              // The backend has already refused to price this order; saving
              // would fail with the same message. Gated until the merchant
              // changes whatever the notice above the summary names.
              disabled={updateLoading || !!previewBlockedReason}
              title={previewBlockedReason ?? undefined}
              data-testid='submit-order'
              className='h-9 px-5 rounded-lg border-0 bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2'
            >
              {updateLoading && (
                <svg
                  className='animate-spin h-4 w-4 text-[rgb(57,57,60)]'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
              )}
              {String(t('update'))}
            </button>
          </div>
        </div>
      </Form>
    );
};

export default EditOrderForm;
